import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { SegInput } from '../../../Components/SegUI/SegInput/SegInputLanding';
import CodigosDeArea from '../../../Assets/CodigosDeArea.json';
import { SegButton } from '../../../Components/SegUI/SegButton/SegButton';
import { useCotizacionStore } from '../../../Services/Providers/CotizacionStore';
import guardarContactoGenerico from '../../../Services/API/GuardarContactoGenerico';
import { SegSelect } from '../../../Components/SegUI/SegSelect/SegSelect';
import { getRecaptchaKey } from '../../../Services/API/helper';
import { useConfig } from '../../../context/ConfigContext';
import { esLeadFallido } from '../../../Services/API/esLeadFallido';
import { ErrorEnvioLead } from '../../../Components/ErrorEnvioLead/ErrorEnvioLead';
import { getWhatsappUrl } from '../../../utils/whatsapp';

interface FormularioProps {
  titulo: string;
  rubro: string;
  origen?: string;
  color?: string;
  extras?: {
    name: string;
    label: string;
    type: 'number' | 'text' | 'select';
    labelGroup?: string;
    data?: {
      value: string;
      label: string;
    }[];
  }[];
  noWpp?: boolean;
}

type FormValues = {
  setCuit: string;
  setRazonSocial: string;
  setNombre: string;
  setApellido: string;
  setPrefijo: string;
  setNumero: string;
  setEmail: string;
  setCodigoPostal: string;
  setLocalidad: string;
} & Record<string, string>;

// ✅ Validación unificada AR/UY
const validarTelefono = (isUY: boolean, prefijo: string, numero: string) => {
  if (isUY) {
    // UY: no se ingresa prefijo; técnicamente se usa '9'. Número: exactamente 8 dígitos.
    return /^[0-9]{8}$/.test(numero || '');
  }
  // AR: prefijo debe estar en catálogo y número 6–9 dígitos. Total prefijo+numero = 10.
  const okPrefijo = CodigosDeArea.includes(prefijo || '');
  const okNumero = /^[0-9]{6,9}$/.test(numero || '');
  const total10 = String(prefijo || '').length + String(numero || '').length === 10;
  return okPrefijo && okNumero && total10;
};

const Formulario = ({ titulo, rubro, color, origen, extras = [], noWpp = false }: FormularioProps) => {
  const { config } = useConfig();
  const isUY = config?.pais === 'UY';

  const [isLoading, setIsLoading] = useState(false);
  const [formularioEnviado, setFormularioEnviado] = React.useState<boolean>(false);
  const [isTelefonoValido, setIsTelefonoValido] = React.useState<boolean>(false);
  const [errorEnvio, setErrorEnvio] = useState<boolean>(false);

  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      setPrefijo: isUY ? '9' : '', // UY: prefijo técnico (no visible)
      setNumero: '',
    },
  });
  const { handleSubmit, control, setValue, getValues, formState, trigger, setError, clearErrors, watch } = methods;
  const { cotizacionValues, setCotizacionValues } = useCotizacionStore();

  // ✅ Helper reactivo
  const validarYActualizarTelefono = () => {
    const { setPrefijo, setNumero } = getValues();
    setIsTelefonoValido(validarTelefono(isUY, setPrefijo, setNumero));
  };

  // ✅ Si cambia país en runtime, ajustar prefijo y revalidar
  useEffect(() => {
    setValue('setPrefijo', isUY ? '9' : (getValues('setPrefijo') || ''));
    validarYActualizarTelefono();
  }, [isUY]);

  const obtenerTokenRecaptcha = (key: any): Promise<string> => {
    return new Promise((resolve) => {
      // @ts-ignore
      grecaptcha.ready(function () {
        // @ts-ignore
        grecaptcha.execute(key!, { action: 'submit' }).then(function (token: string) {
          setCotizacionValues({ ...cotizacionValues, captchaToken: token });
          resolve(token);
        });
      });
    });
  };

  const onSubmit: SubmitHandler<FormValues> = async(values: FormValues) => {
    setIsLoading(true);
    setErrorEnvio(false);

    const { setCuit, setRazonSocial, setNombre, setApellido, setPrefijo, setNumero, setEmail, setCodigoPostal, setLocalidad, ...restExtras } = values;

    const extrasValues = Object.entries(restExtras).reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {} as Record<string, any>);

    const updatedValues = {
      ...cotizacionValues,
      cuit: setCuit,
      razonSocial: setRazonSocial,
      nombre: setNombre,
      apellido: setApellido,
      telefono: {
        prefijo: isUY ? '9' : setPrefijo,
        numero: setNumero,
      },
      email: (setEmail || '').toLowerCase(),
      codigoPostal: setCodigoPostal,
      localidadLanding: setLocalidad,
      rubro: rubro,
      tipoDoc: { value: 'CUIT', label: 'CUIT' },
    };

    const key = await getRecaptchaKey();
    obtenerTokenRecaptcha(key).then((token: string) => {
      const fetchData = async () => {
        const response: any = await guardarContactoGenerico({ ...updatedValues, captchaToken: token }, origen, extrasValues, config?.pais);
        if (!esLeadFallido(response)) {
          if (response.data.api_status === 200 && response.data.api_data !== null) {
            setCotizacionValues({
              ...updatedValues,
              contactoId: response.data.api_data.idContacto,
              token: response.data.api_data.token,
              captchaToken: token,
            });
            setFormularioEnviado(true);
            setIsLoading(false);
            document.getElementById('formulario')?.scrollIntoView({ behavior: 'smooth' });
          } else if (response.data.api_data === 'El Captcha no es válido') {
            setIsLoading(false);
            setErrorEnvio(true);
            console.log('Error en el captcha');
          } else {
            console.error('Error al guardar contacto');
            setIsLoading(false);
            setErrorEnvio(true);
          }
        } else {
          setIsLoading(false);
          setErrorEnvio(true);
        }
      };
      fetchData();
    });
  };

  return (
    <div id="formulario">
      {!formularioEnviado && (
        <div className="py-40 py-lg-50 bg-f3">
          <div className="container px-18">
            <h2 className="ff-mukta fw-bold fs-30 fs-lg-40 text-black text-center mb-18 mb-lg-40">{titulo}</h2>
            <div className="bg-white rounded-18 shadow-4 px-30 px-lg-60 py-30 py-lg-40">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row gx-lg-5 align-items-end">
                  {/* CUIT */}
                  <div className="col-lg-6">
                    <div className="mb-20 mb-lg-35">
                      <div className="fs-20 fw-bold text-black mb-1">Ingresá el {isUY ? "RUT" : "CUIT"}</div>
                      <div className="fs-16 text-6e mb-1">Sin guiones</div>
                      <Controller
                        name="setCuit"
                        control={control}
                        rules={{
                          validate: {
                            required: (value) => !!value || (isUY ? 'Ingresá el RUT' : 'Ingresá el CUIT'),
                            onlyNumbers: (value) => /^[0-9]+$/.test(value || '') || 'Solo se permiten números',
                            pattern:
                              (value) => {
                                if (isUY) {
                                  if (!/^\d{11,12}$/.test(value)) return "RUT inválido";
                                } else {
                                  if (!/^(20|2[3-7]|30|3[3-4])\d{8}[0-9]$/.test(value)) {
                                    return "CUIT inválido"
                                  }
                                }
                              }
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <SegInput
                            id="cuit"
                            className="fs-16"
                            label=""
                            name="cuit"
                            type="text"
                            inputMode="numeric"
                            width={100}
                            placeHolder="Debe tener 11 dígitos"
                            onChange={(e) => {
                              field.onChange(e);
                              setValue('setCuit', e.target.value);
                              trigger('setCuit');
                            }}
                            value={field.value}
                            autoFocus={false}
                            leyendaError={fieldState?.error?.message}
                            borderColor={`${formState.errors.setCuit ? 'var(--bs-form-invalid-border-color)' : ''}`}
                            focusBorderColor={`${formState.errors.setCuit ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                            absolut
                            isLandingForm
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Razón Social */}
                  <div className="col-lg-6">
                    <div className="mb-20 mb-lg-35">
                      <div className="fs-20 fw-bold text-black mb-1">Razón Social</div>
                      <Controller
                        name="setRazonSocial"
                        control={control}
                        rules={{
                          validate: {
                            required: (value) => (typeof value === 'string' ? value.trim() !== '' || 'Ingresá la razón social' : !!value || 'Ingresá la razón social'),
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <SegInput
                            className="fs-16"
                            label=""
                            name="setRazonSocial"
                            type="text"
                            width={100}
                            placeHolder="EJ: Perez SRL"
                            onChange={(e) => {
                              field.onChange(e);
                              setValue('setRazonSocial', e.target.value);
                              trigger('setRazonSocial');
                            }}
                            value={field.value}
                            autoFocus={false}
                            leyendaError={fieldState?.error?.message}
                            borderColor={`${formState.errors.setRazonSocial ? 'var(--bs-form-invalid-border-color)' : ''}`}
                            focusBorderColor={`${formState.errors.setRazonSocial ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                            absolut
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Nombre */}
                  <div className="col-lg-6">
                    <div className="mb-20 mb-lg-35">
                      <div className="fs-20 fw-bold text-black mb-1">Nombre</div>
                      <Controller
                        name="setNombre"
                        control={control}
                        rules={{
                          validate: {
                            required: (value) => (typeof value === 'string' ? value.trim() !== '' || 'Ingresá el nombre' : !!value || 'Ingresá el nombre'),
                            pattern: (value) => /^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s]+$/.test((value || '').trim()) || 'Nombre inválido',
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <SegInput
                            className="fs-16"
                            label=""
                            name="setNombre"
                            type="text"
                            width={100}
                            placeHolder="Ej: Juan"
                            onChange={(e) => {
                              field.onChange(e);
                              setValue('setNombre', e.target.value);
                              trigger('setNombre');
                            }}
                            value={field.value}
                            autoFocus={false}
                            leyendaError={fieldState?.error?.message}
                            borderColor={`${formState.errors.setNombre ? 'var(--bs-form-invalid-border-color)' : ''}`}
                            focusBorderColor={`${formState.errors.setNombre ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                            absolut
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Apellido */}
                  <div className="col-lg-6">
                    <div className="mb-20 mb-lg-35">
                      <div className="fs-20 fw-bold text-black mb-1">Apellido</div>
                      <Controller
                        name="setApellido"
                        control={control}
                        rules={{
                          validate: {
                            required: (value) => (typeof value === 'string' ? value.trim() !== '' || 'Ingresá el apellido' : !!value || 'Ingresá el apellido'),
                            pattern: (value) => /^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s]+$/.test((value || '').trim()) || 'Apellido inválido',
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <SegInput
                            className="fs-16"
                            label=""
                            name="setApellido"
                            type="text"
                            width={100}
                            placeHolder="Ej: García"
                            onChange={(e) => {
                              field.onChange(e);
                              setValue('setApellido', e.target.value);
                              trigger('setApellido');
                            }}
                            value={field.value}
                            leyendaError={fieldState?.error?.message}
                            borderColor={`${formState.errors.setApellido ? 'var(--bs-form-invalid-border-color)' : ''}`}
                            focusBorderColor={`${formState.errors.setApellido ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                            absolut
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Teléfono celular */}
                  <div className="col-lg-6">
                    <div className="mb-20 mb-lg-35">
                      <div className="fs-20 fw-bold text-black mb-1">Teléfono celular</div>
                      <div className={`row g-2 ${isUY ? 'align-items-end' : ''}`}>
                        {/* Cód. área: solo AR */}
                        {!isUY && (
                          <div className="col-4">
                            <div className="fs-16 text-6e mb-1">Cód. área</div>
                            <Controller
                              name="setPrefijo"
                              control={control}
                              rules={{
                                validate: {
                                  required: (value) => !!value || 'Ingresá el Cód. Área',
                                  onlyNumbers: (value) => /^[0-9]+$/.test(value || '') || 'Solo se permiten números',
                                  isInValidCodeArea: (value) => CodigosDeArea.includes(value || '') || 'Cód. Área inválido',
                                },
                              }}
                              render={({ field, fieldState }) => (
                                <SegInput
                                  isGroup={true}
                                  labelGroup="0"
                                  className="fs-16"
                                  label=""
                                  name="setPrefijo"
                                  type="text"
                                  inputMode="numeric"
                                  width={100}
                                  placeHolder="11"
                                  value={field.value}
                                  autoFocus={false}
                                  onKeyDown={(e) => {
                                    if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') e.preventDefault();
                                  }}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    const prefijo = e.target.value;
                                    setValue('setPrefijo', prefijo);
                                    trigger('setPrefijo');
                                    const numero = getValues('setNumero');
                                    if (numero !== '' && !validarTelefono(false, prefijo, numero)) {
                                      setError('setNumero', { type: 'validate', message: 'Número inválido' });
                                    } else {
                                      clearErrors('setNumero');
                                    }
                                    validarYActualizarTelefono();
                                  }}
                                  leyendaError={fieldState?.error?.message}
                                  borderColor={`${formState.errors.setPrefijo ? 'var(--bs-form-invalid-border-color)' : ''}`}
                                  focusBorderColor={`${formState.errors.setPrefijo ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                                  absolut
                                />
                              )}
                            />
                          </div>
                        )}

                        {/* Número */}
                        <div className={isUY ? 'col-12' : 'col-8'}>
                          {!isUY && <div className="fs-16 text-6e mb-1">Número de teléfono</div>}
                          <Controller
                            name="setNumero"
                            control={control}
                            rules={{
                              validate: {
                                required: (value) => !!value || 'Ingresá Número de Celular',
                                onlyNumbers: (value) => /^[0-9]+$/.test(value || '') || 'Solo se permiten números',
                                pattern: (value) => (isUY ? /^[0-9]{8}$/.test(value || '') : /^[0-9]{6,9}$/.test(value || '')) || 'Número inválido',
                                validateCombination: (value) => {
                                  if (isUY) return true; // en UY no se combina con prefijo ingresado
                                  const prefijo = watch('setPrefijo');
                                  return (String(prefijo || '') + String(value || '')).length === 10 || 'Número inválido';
                                },
                              },
                            }}
                            render={({ field, fieldState }) => (
                              <SegInput
                                isGroup={true}
                                labelGroup={isUY ? '9' : '15'}
                                className="fs-16"
                                label=""
                                name="setNumero"
                                type={isUY ? 'tel' : 'text'}
                                inputMode="numeric"
                                width={100}
                                placeHolder={isUY ? '33669955' : '33669955'}
                                value={field.value}
                                autoFocus={false}
                                onKeyDown={(e) => {
                                  if (!isUY && (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-')) e.preventDefault();
                                }}
                                onChange={(e) => {
                                  field.onChange(e);
                                  setValue('setNumero', e.target.value);
                                  trigger('setNumero');
                                  validarYActualizarTelefono();
                                }}
                                leyendaError={fieldState?.error?.message}
                                borderColor={`${formState.errors.setNumero ? 'var(--bs-form-invalid-border-color)' : ''}`}
                                focusBorderColor={`${formState.errors.setNumero ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                                absolut
                              />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="col-lg-6">
                    <div className="mb-20 mb-lg-35">
                      <div className="fs-20 fw-bold text-black mb-1">Email</div>
                      <Controller
                        name="setEmail"
                        control={control}
                        rules={{
                          validate: {
                            required: (value) => !!value || 'Ingresá el email',
                            pattern: (value) => /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(value || '') || 'Email inválido',
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <SegInput
                            className="fs-16"
                            label=""
                            name="setEmail"
                            type="text"
                            width={100}
                            placeHolder={isUY ? "Ej: nombre@dominio.com.uy" : "Ej: nombre@dominio.com.ar"}
                            onChange={(e) => {
                              field.onChange(e);
                              setValue('setEmail', e.target.value);
                              trigger('setEmail');
                            }}
                            value={field.value}
                            autoFocus={false}
                            leyendaError={fieldState?.error?.message}
                            borderColor={`${formState.errors.setEmail ? 'var(--bs-form-invalid-border-color)' : ''}`}
                            focusBorderColor={`${formState.errors.setEmail ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                            absolut
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Código Postal */}
                  <div className="col-lg-6">
                    <div className="mb-20 mb-lg-35">
                      <div className="fs-20 fw-bold text-black mb-1">Código Postal</div>
                      <Controller
                        name="setCodigoPostal"
                        control={control}
                        rules={{
                          validate: {
                            required: (value) => !!value || 'Ingresá el Código Postal',
                            onlyNumbers: (value) => /^[0-9]+$/.test(value || '') || 'Solo se permiten números',
                            pattern: (value) => (config?.pais === 'UY' ? /^[0-9]{5}$/.test(value || '') : /^[0-9]{4}$/.test(value || '')) || 'Número inválido',
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <SegInput
                            isGroup={false}
                            className="fs-16"
                            label=""
                            name="setCodigoPostal"
                            type="text"
                            inputMode="numeric"
                            width={100}
                            placeHolder=""
                            value={field.value}
                            autoFocus={false}
                            onChange={(e) => {
                              field.onChange(e);
                              setValue('setCodigoPostal', e.target.value);
                              trigger('setCodigoPostal');
                            }}
                            leyendaError={fieldState?.error?.message}
                            borderColor={`${formState.errors.setCodigoPostal ? 'var(--bs-form-invalid-border-color)' : ''}`}
                            focusBorderColor={`${formState.errors.setCodigoPostal ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                            absolut
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Localidad */}
                  <div className="col-lg-6">
                    <div className="mb-20 mb-lg-35">
                      <div className="fs-20 fw-bold text-black mb-1">Localidad</div>
                      <Controller
                        name="setLocalidad"
                        control={control}
                        rules={{
                          validate: {
                            required: (value) => (typeof value === 'string' ? value.trim() !== '' || 'Ingresá la localidad' : !!value || 'Ingresá la localidad'),
                            pattern: (value) => {
                              if (/^\d+$/.test(value || '')) return 'La localidad no puede contener solo números';
                              return /^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ0-9\s]+$/.test(value || '') || 'Localidad inválida';
                            },
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <SegInput
                            className="fs-16"
                            label=""
                            name="setLocalidad"
                            type="text"
                            width={100}
                            placeHolder=""
                            onChange={(e) => {
                              field.onChange(e);
                              setValue('setLocalidad', e.target.value);
                              trigger('setLocalidad');
                            }}
                            value={field.value}
                            autoFocus={false}
                            leyendaError={fieldState?.error?.message}
                            borderColor={`${formState.errors.setLocalidad ? 'var(--bs-form-invalid-border-color)' : ''}`}
                            focusBorderColor={`${formState.errors.setLocalidad ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                            absolut
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Extras dinámicos */}
                  {extras.map((extra) => (
                    <div className="col-lg-6" key={extra.name}>
                      <div className="mb-20 mb-lg-35">
                        <div className="fs-20 fw-bold text-black mb-1">{extra.label}</div>
                        {extra.type === 'select' && extra.data ? (
                          <FormProvider {...methods}>
                            <SegSelect
                              id={extra.name}
                              selected={extra.data.find((opt) => opt.value === watch(extra.name)) || null}
                              name={extra.name}
                              options={extra.data || []}
                              isGrupable
                              width={'100%'}
                              openDefault={false}
                              borderColor="#c5c5c5"
                            />
                          </FormProvider>
                        ) : (
                          <Controller
                            name={extra.name as any}
                            control={control}
                            rules={{
                              validate: {
                                required: (value) => {
                                  if (extra.type !== 'number') return typeof value === 'string' ? value.trim() !== '' || 'Ingresá ' + extra.label : !!value || 'Ingresá ' + extra.label;
                                  return !!value || 'Ingresá ' + extra.label;
                                },
                                onlyNumbers: (value) => (extra.type !== 'number' ? true : /^[0-9]+$/.test(value || '') || 'Solo se permiten números'),
                              },
                            }}
                            render={({ field, fieldState }) => (
                              <SegInput
                                isGroup={!!extra.labelGroup}
                                labelGroup={extra.labelGroup}
                                className="fs-16"
                                label=""
                                name={extra.name}
                                type={'text'}
                                inputMode={extra.type === 'number' ? 'numeric' : 'text'}
                                width={100}
                                onChange={(e) => {
                                  field.onChange(e);
                                  setValue(extra.name as any, e.target.value);
                                  trigger(extra.name as any);
                                }}
                                value={field.value}
                                leyendaError={fieldState?.error?.message}
                                borderColor={`${formState.errors[extra.name] ? 'var(--bs-form-invalid-border-color)' : ''}`}
                                focusBorderColor={`${formState.errors[extra.name] ? 'var(--bs-form-invalid-border-color)' : '#2c7dfd'}`}
                                absolut
                              />
                            )}
                          />
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Botón */}
                  <div className="text-center pt-3">
                    <SegButton
                      label="Cotizar"
                      type="submit"
                      className="btn btn-primary fs-20 fs-lg-25 fw-bold w-200p w-lg-250p"
                      disabled={!formState.isValid}
                      loading={isLoading}
                      spinnerSize="small"
                      bgColor={color}
                    />
                    {errorEnvio && <ErrorEnvioLead whatsappHref={getWhatsappUrl(config?.pais)} />}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {formularioEnviado && (
        <div className="py-40 py-lg-50 bg-f3">
          <div className="container px-18">
            <h2 className={`ff-mukta fw-bold fs-30 fs-lg-40 text-center mb-7 mb-lg-12 ${!color && 'text-primary'}`} style={{ color }}>
              ¡Recibimos tu solicitud!
            </h2>
            <div className="col-10 col-lg-12 mx-auto">
              <p className="fs-16 fs-lg-20 fw-medium lh-13 text-black text-center mb-25 mb-lg-30">
                En breve nos comunicaremos con vos para asesorarte en la mejor cobertura para tu empresa.
              </p>
            </div>
            <div className="bg-white rounded-18 shadow-4 px-30 px-lg-60 py-30 py-lg-40">
              <dl className="row fs-16 fs-lg-18 mb-0">
                <div className="col-md-6">
                  <dt className="fw-normal text-primary">
                    <svg width="27" height="27" className="svg-icon me-10" style={{ color }}>
                      <use xlinkHref="#card-lin"></use>
                    </svg>
                    <span className="text-dark">Razón social:</span>
                  </dt>
                  <dd className="fw-bold lh-12 ps-40 mb-20 mb-lg-30">{cotizacionValues.razonSocial}</dd>
                </div>
                <div className="col-md-6">
                  <dt className="fw-normal text-primary">
                    <svg className="svg-icon me-10" width="27" height="27" style={{ color }}>
                      <use xlinkHref="#tel-lin"></use>
                    </svg>
                    <span className="text-dark">Teléfono:</span>
                  </dt>
                  <dd className="fw-bold lh-12 ps-40 mb-20 mb-lg-30">
                    {cotizacionValues.telefono?.prefijo && cotizacionValues.telefono?.numero
                      ? cotizacionValues.telefono?.prefijo + cotizacionValues.telefono?.numero
                      : ''}
                  </dd>
                </div>
                <div className="col-md-6">
                  <dt className="fw-normal text-primary">
                    <svg className="svg-icon me-10" width="27" height="27" style={{ color }}>
                      <use xlinkHref="#envelope-lin"></use>
                    </svg>
                    <span className="text-dark">Email:</span>
                  </dt>
                  <dd className="fw-bold lh-12 ps-40 mb-20 mb-lg-30">{cotizacionValues.email}</dd>
                </div>
                <div className="col-md-6">
                  <dt className="fw-normal text-primary">
                    <svg className="svg-icon me-10" width="27" height="27" style={{ color }}>
                      <use xlinkHref="#person-lin"></use>
                    </svg>
                    <span className="text-dark">{isUY ? "RUT:" : "CUIT:"}</span>
                  </dt>
                  <dd className="fw-bold lh-12 ps-40 mb-20 mb-lg-30">{cotizacionValues.cuit}</dd>
                </div>
                <div className="col-md-6">
                  <dt className="fw-normal text-primary">
                    <svg className="svg-icon me-10" width="27" height="27" style={{ color }}>
                      <use xlinkHref="#location-lin"></use>
                    </svg>
                    <span className="text-dark">Localidad:</span>
                  </dt>
                  <dd className="fw-bold lh-12 ps-40 mb-20 mb-lg-30">{cotizacionValues.localidadLanding}</dd>
                </div>
              </dl>
              {noWpp && (
                <p className="fs-17 fw-medium text-center mb-0">
                  ¿Querés continuar por WhatsApp?
                  <span> </span>
                  <a
                    href="https://api.whatsapp.com/send/?phone=5491128357048&text&type=phone_number&app_absent=0"
                    target="_blank"
                    rel="noreferrer"
                    className="d-inline-flex align-items-center fs-17 text-black"
                  >
                    <span>Chatéanos</span>
                    <svg className="svg-icon ms-2 w-12em">
                      <use xlinkHref="#whatsapp"></use>
                    </svg>
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Formulario;
